import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ExternalLink, FileUp, MoreHorizontal, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { formatHumanDate, formatCurrency } from '@/lib/format.js';
import { ExperienceModelDialog } from '@/components/account/ExperienceModelDialog.jsx';
import PhfSubmissionSheet from '@/components/sheets/PhfSubmissionSheet.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { useShellStore } from '@/stores/shell-store.js';
import useDeal from '@/hooks/useDeal.js';
import useCommercialDeal from '@/hooks/useCommercialDeal.js';
import useFleetCoverage from '@/hooks/useFleetCoverage.js';
import useDealFleetAndRoster from '@/hooks/useDealFleetAndRoster.js';
import useDealActions, { primaryActionFor } from '@/hooks/useDealActions.js';
import { updateDealStage, fileDealDocuments, announceDocumentsChanged, announceLedgerChanged } from '@/services/deals.service.js';
import useDocumentDrop, { itemOf } from '@/hooks/useDocumentDrop.js';
import DocumentDropRows from './DocumentDropRows.jsx';
import { getAccountTerms } from '@/services/account.service.js';
import { KIND_LABELS } from './record/lineage.js';
import StageBadge from '@/components/shared/StageBadge.jsx';
import TestBadge from '@/components/shared/TestBadge.jsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu.jsx';
import { stageWord } from '@/lib/stage-word.js';
import { NamedInsuredCard, CoverageTargetsCard, MarketsFactStrip } from './DealCockpitCards.jsx';

import FleetPanel from './FleetPanel.jsx';
import RosterPanel from './RosterPanel.jsx';
import SubmissionPanel from './SubmissionPanel.jsx';
import SubmissionBuilderPanel from './SubmissionBuilderPanel.jsx';
import HangarLocationsPanel from './HangarLocationsPanel.jsx';
import HangarAnnualReviewPanel from './HangarAnnualReviewPanel.jsx';
import CommercialOperationsPanel from './CommercialOperationsPanel.jsx';
import CommercialCoveragePanel from './CommercialCoveragePanel.jsx';
import CommsTimelinePanel from './CommsTimelinePanel.jsx';
import PaperworkTab from './PaperworkTab.jsx';
import TransactionHistoryPanel from './TransactionHistoryPanel.jsx';
import TrustLedgerPanel from './TrustLedgerPanel.jsx';
import DealCockpitRail from './DealCockpitRail.jsx';
import AdditionalInterestsPanel from './AdditionalInterestsPanel.jsx';
import IntakeReadinessCard from './IntakeReadinessCard.jsx';
import AddressChangePendingBanner from './AddressChangePendingBanner.jsx';
import MarketsWorkSurface from './MarketsWorkSurface.jsx';
import DealQueuePanel from './DealQueuePanel.jsx';
import DealModelsPanel from './DealModelsPanel.jsx';
import MoneyStrip from './MoneyStrip.jsx';
import ClientPickCard from './ClientPickCard.jsx';
import EffectiveLockKey from './EffectiveLockKey.jsx';
import DealFeesKey from './DealFeesKey.jsx';
import DealPolicyNumberKey from './DealPolicyNumberKey.jsx';
import DealProducerKey from './DealProducerKey.jsx';
import DealReopenKey from './DealReopenKey.jsx';
import DealCancellationNote from './DealCancellationNote.jsx';
import BrProjectPanel from './BrProjectPanel.jsx';
import BrStatusPanel from '@/components/sheets/BrStatusPanel.jsx';
import CaStatusPanel from '@/components/sheets/CaStatusPanel.jsx';
import BrMarketQuotesPanel from '@/components/sheets/BrMarketQuotesPanel.jsx';
import DealActionsSheets from '@/components/sheets/DealActionsSheets.jsx';
import QuoteParseReviewSheet from '@/components/sheets/QuoteParseReviewSheet.jsx';
import PresentQuotesSheet from '@/components/sheets/PresentQuotesSheet.jsx';
import PresentedStrip from '@/components/account/PresentedStrip.jsx';
import { uploadQuoteFile } from '@/services/quoteIngest.service.js';
import { doorForItem } from '@/services/queue.service.js';
import { parseBrData, brStatusWord } from '@/lib/br-data.js';
import { resolveLine } from '@/lib/insurance-line.js';
import { ENDED_BECAUSE_WORDS } from '@/components/v3/words.js';

// Needs-you items whose exact door is a sheet on this very workspace
// (mirrors DealQueuePanel's LOCAL_DOOR). Everything else navigates via
// doorForItem(item).href. Ship 4 replaces the feed source with /needs-you.
const NEEDS_LOCAL_DOOR = {
  paper_draft: 'paperwork',
  escalation: 'rtq',
  parked_intake: 'resume_link',
  quote_expiring: 'quotes',
  quote_draft: 'review_quote',
  // Decision 8: a pilot change since the quote opens its sheet in place (openers.bind_hold).
  bind_held: 'bind_hold',
};

// Deal workspace, four-zone cutover (MV-10 3.2, Ship 4a). Every deal renders
// the SAME four fixed zones in order:
//   1. WHERE IS IT      — derived stage strip + THE one primary action +
//                          readiness meter pre-shop + market ladder once
//                          dispatched (+ BR status on Property).
//   2. WHAT NEEDS ME    — this deal's what's-waiting queue items, inline.
//   3. IS THE MONEY RIGHT — interim ledger strip (invoiced/paid/owed, honest
//                          method labels); MV-08 (Ship 5) lands the full
//                          three-pane + verdict behind the same seam.
//   4. SHOW ME THE PAPER: the Paperwork tab (the server's checklist, the filed list, the files).
// Machinery, history, and escape hatches fold one layer down (collapsible
// groups below the zones + the existing bottom-sheet layer). The tab strip
// is gone: one screen shape, learned once. No stage-keyed visibility hacks;
// zones always render, empties say what will appear.

// Legacy emphasis set from the tabbed era. The zone-1 primary now comes from
// PRIMARY_BY_STATE (useDealActions); this set survives as its fallback
// ordering when a state has no ruled primary.
const PRIMARY_KEYS = new Set(['send', 'quote']);

// Manual terminal moves — the ONLY human-set lifecycle states
// (DEAL-LIFECYCLE-SPEC Phase 1). Everything else is derived.
const TERMINAL_MOVES = ['Lost', 'Cancelled', 'Expired'];
// "Not renewed: aircraft sold" (main-fixes 2026-10-29): aircraft sold is a REASON. At the end of the term: Lost, not renewed, reason
// aircraft sold (ended_because aircraft_sold; the server refuses it mid-term, where a sale is the LPR cancellation with reason
// Aircraft sold, and to the shared login). It never shows as a possible lapse.
const BECAUSE_MOVES = [{ stage: 'Lost', because: 'aircraft_sold', label: ENDED_BECAUSE_WORDS.aircraft_sold }];

// Aviation-only action doors hidden on Property deals (Ship 4 item 5).
const PROPERTY_HIDDEN_ACTIONS = new Set(['edit_roster', 'quote_diff', 'add_pilot', 'rtq']);

// ── Workspace drop router (prompt 173; paperwork-1 D7, A18) ────────────────
// One fallback target, zero assumptions: a file dropped on open workspace
// ASKS what it is. A filename match may PRESELECT an answer (lib/dropKinds
// suggestDropKind); it never routes without the broker confirming. Declared
// zones (the interest cert slot, the doc sheets) claim their own drops and stop
// propagation. The kinds, their lines and the derived name are screen 2's
// (DocumentDropRows); a Quote goes to the quote parse, every other kind to the
// deal's document door (fileDealDocuments).

function renderPanel(name, ctx) {
  switch (name) {
    case 'SubmissionBuilderPanel':
      return (
        <SubmissionBuilderPanel
          key="cockpit"
          dealId={ctx.dealId}
          fleetAndRoster={ctx.fleetAndRoster}
          documents={ctx.documents}
          accountId={ctx.accountId}
          onChange={ctx.onChange}
          onOpenDocs={ctx.onOpenDocs}
          onOpenTrust={ctx.onOpenTrust}
          lob={ctx.lob}
        />
      );
    case 'SubmissionPanel':
      return <SubmissionPanel key="hangar-submission" dealId={ctx.dealId} />;
    case 'CommercialCoveragePanel':
      return (
        <CommercialCoveragePanel
          key="commercial-coverage"
          dealId={ctx.dealId}
          manifest={ctx.commercial?.data?.manifest}
          loading={ctx.commercial?.loading}
        />
      );
    case 'CommercialOperationsPanel':
      return (
        <CommercialOperationsPanel
          key="commercial-ops"
          manifest={ctx.commercial?.data?.manifest}
          loading={ctx.commercial?.loading}
        />
      );
    case 'FleetPanel':
      return (
        // Anchor for the readiness "Fix" door on aircraft blockers.
        <div id="deal-fleet" key="fleet-anchor" className="scroll-mt-24">
        <FleetPanel
          key="fleet"
          fleet={ctx.fleetAndRoster?.fleet}
          dealId={ctx.dealId}
          coverage={ctx.fleetCoverage?.byTail}
          bases={ctx.fleetCoverage?.bases}
          editable={ctx.fleetCoverage?.editable}
          onCoverageSaved={ctx.onCoverageSaved}
        />
        </div>
      );
    case 'RosterPanel':
      return (
        // Anchor for the queue's pilot_unlinked door (the holding strip lives here).
        <div id="deal-roster" key="roster-anchor" className="scroll-mt-24">
        <RosterPanel key="roster" roster={ctx.fleetAndRoster?.roster} dealId={ctx.dealId} onChange={ctx.onChange} />
        </div>
      );
    case 'HangarLocationsPanel':
      return <HangarLocationsPanel key="hangar-locations" dealId={ctx.dealId} />;
    case 'HangarAnnualReviewPanel':
      return <HangarAnnualReviewPanel key="hangar-annual-review" dealId={ctx.dealId} accountId={ctx.accountId} />;
    case 'BrProjectPanel':
      return <BrProjectPanel key="br-project" deal={ctx.deal} />;
    default:
      return null;
  }
}

// A strip that carries nothing yet (money, paperwork pre-bind) or holds
// one-layer-down machinery (workbench, comms, trust). Rendered as a one-line
// strip, never hidden, expandable on click. Children mount only once expanded
// so a dormant strip costs no fetch. `id` lets cross-surface doors scroll to it.
// 199: THE ANCHOR SURVIVES THE COLLAPSE.
//
// This rendered `{open && children}` while the #zone-paper / #zone-money ids
// sat ON THE CHILDREN, so a closed strip had no anchor in the DOM at all and
// two scroll doors landed on nothing. The ids now ride the WRAPPER, which is
// always rendered. The collapse was never the defect; removing the address was.
//
// `forceOpen` is the second half: scrolling to a collapsed strip OPENS it.
// Landing on a closed box is the same dead end one layer in. It only ever opens
// (never forces closed), so the broker can still collapse it afterwards.
function DormantStrip({ id, title, closedLabel = 'nothing here until binding', forceOpen = false, children }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  return (
    <div id={id} className="scroll-mt-24 rounded-card border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">{title}</span>
        <span className="text-[11px] text-text-muted">{open ? 'hide' : closedLabel}</span>
      </button>
      {open && <div className="space-y-4 border-t border-border px-3.5 py-3">{children}</div>}
    </div>
  );
}

export default function DealWorkspace({ data, dealId, onChange }) {
  // Hooks FIRST (before any data guard) — hooks-before-conditional-return.
  const setRail = useShellStore((s) => s.setRail);
  const clearRail = useShellStore((s) => s.clearRail);

  const { data: dealData, refresh: refreshDeal } = useDeal(dealId || null);
  const deal = dealData?.deal || null;
  // Declared up here with the other data hooks so handleChange (below) can
  // refresh it without reaching a `const` in its temporal dead zone.
  const dealFleetAndRoster = useDealFleetAndRoster(dealId);
  // Line resolution (AIRPORT-PROPERTY-LINE-REPAIR Ship 0): the picklist was
  // consolidated to "Airport Property" on 2026-07-12; retired literals and
  // the BR-data-presence fallback all resolve through the shared resolver.
  // Presence of BR data is the fact, the picklist is the label.
  const line = resolveLine(deal);
  const insuranceLine = line.canonical;
  const isCommercial = line.isCommercial;
  // Whole family (occupied hangar + builders risk): hides aviation doors,
  // suppresses the aviation readiness meter.
  const isProperty = line.isProperty;
  const isBuildersRisk = line.isBuildersRisk;
  const isHangar = line.isHangar;
  // Aviation deals route quote entry into the markets ladder (Ship 3): quick-log
  // for new, Quote detail for existing. BR/Property/Commercial/Hangar keep the
  // old QuoteEntrySheet until their own pass.
  const isAviation = line.isAviation;
  const commercial = useCommercialDeal(isCommercial ? dealId : null);
  const actionState = useDealActions(dealId);

  const [railOpen, setRailOpen] = useState(true);

  // MV-03 front door A: drop a quote PDF anywhere on the deal → the pipeline
  // parses it into a DRAFT and the review screen opens (drop opens the door,
  // MV-10 — never a silent write). The hidden input is the click path twin.
  const [dragDepth, setDragDepth] = useState(0);
  const [parsingQuote, setParsingQuote] = useState(false);
  const [reviewQuoteId, setReviewQuoteId] = useState(null);
  const [presentOpen, setPresentOpen] = useState(false);
  const [intakeViewOpen, setIntakeViewOpen] = useState(false);
  const quoteFileInputRef = useRef(null);

  // The PDF rule is the server's (utils/document-kind-rules.js, asked of the bytes by the drop-pdf door and of the file by the
  // drop's preview): no copy here. A failure is said once and THROWN, so a drop row says it failed (review of 2026-11-02, I5).
  const handleQuoteFile = useCallback(async (file) => {
    if (!file || !dealId) return;
    setParsingQuote(true);
    try {
      const res = await uploadQuoteFile(dealId, file);
      const parse = res.parse || {};
      if (parse.enriched) {
        // Attach-over-create: the parse enriched an existing quote (no sibling).
        // Say exactly what happened, never "sorta kinda worked".
        const added = parse.fields_added?.length || 0;
        const skipped = parse.fields_skipped?.length || 0;
        const who = parse.carrier_name ? `${parse.carrier_name} quote` : 'the existing quote';
        if (added > 0) {
          toast.success(`Accepted into ${who}: ${added} field${added === 1 ? '' : 's'} added${skipped ? `, ${skipped} kept (already set)` : ''}`);
        } else {
          toast.success(`Attached to ${who}: nothing new to add (all fields already set)`);
        }
        if (typeof onChange === 'function') onChange();
        refreshDeal();
      } else if (parse.staged && parse.quote_id) {
        setReviewQuoteId(parse.quote_id);
        toast.success(`Parsed ${parse.options_created || ''} option${parse.options_created === 1 ? '' : 's'} — review to approve`);
      } else if (parse.duplicate_of) {
        toast.success('Already captured: these terms match an existing quote on this deal');
      } else if (parse.not_quote) {
        toast.error(`That reads as "${parse.type}", not a quote — it landed in the inbound queue`);
      } else if (parse.triage) {
        toast.error('Parsed, but could not confirm the deal match — see the Quote inbox');
      } else {
        toast.error('Parse did not stage a draft — see the Quote inbox');
      }
    } catch (err) {
      toast.error(err.message || 'Quote parse failed (the failure alert has fired)');
      throw err;
    } finally {
      setParsingQuote(false);
    }
  }, [dealId, onChange, refreshDeal]);

  // Drop router state: the dropped files and their answers (useDocumentDrop); the dialog is open while any are held.
  const drop = useDocumentDrop(dealId);
  const [dropOpen, setDropOpen] = useState(false);
  const [routingDrop, setRoutingDrop] = useState(false);
  const { add: addDropped, clear: clearDropped } = drop;
  const openDropRouter = useCallback((fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    addDropped(files);
    setDropOpen(true);
  }, [addDropped]);
  const closeDropRouter = useCallback(() => { setDropOpen(false); clearDropped(); }, [clearDropped]);
  // Every file routes and every file reports (prompt 173 §2c), on its row and as a toast: a Quote to the quote parse, the
  // rest to the document door in one request, each answer kept on its row (the filed name, "Wrong type? Change it").
  const routeDroppedFiles = useCallback(async () => {
    const pending = drop.pending; // settled files (filed, or already filed with every fact stated) are never posted
    setRoutingDrop(true);
    try {
      for (const e of pending.filter((x) => x.kind === 'quote')) {
        try { await handleQuoteFile(e.file); drop.set(e.key, { result: { ok: true, quote: true } }); }
        catch (err) { drop.set(e.key, { result: { ok: false, error: err?.message || 'the quote parse failed' } }); }
      }
      const docs = pending.filter((x) => x.kind !== 'quote');
      if (docs.length) {
        try {
          const res = await fileDealDocuments(dealId, docs.map(itemOf));
          drop.applyResults(docs, res.results);
          for (const r of res.results) {
            if (r.ok) toast.success(`${r.filename} filed as ${r.document?.name || r.name}`);
            else toast.error(`${r.filename}: ${r.error}`);
          }
          // The deal's document lists and the ledger's carrier documents re-read (never the state from before the drop).
          if (res.results.some((r) => r.ok)) { announceDocumentsChanged(dealId); announceLedgerChanged(dealId); }
        } catch (err) {
          toast.error(err?.message || 'The documents door did not answer; nothing was filed');
        }
      }
      refreshDeal();
    } finally {
      setRoutingDrop(false);
    }
  }, [drop, dealId, handleQuoteFile, refreshDeal]);
  const dropPending = drop.pending.length;

  // Stage badge "why" popover (DEAL-LIFECYCLE-SPEC Phase 1): the badge
  // explains, it does not command. Manual moves are terminal-only + reason.
  const [whyOpen, setWhyOpen] = useState(false);
  const [reasonStage, setReasonStage] = useState(null); // terminal stage awaiting a typed reason
  const [reasonText, setReasonText] = useState('');
  const [reasonBecause, setReasonBecause] = useState(null); // a stated reason the term ended (aircraft_sold)
  const [pendingStage, setPendingStage] = useState(null);

  // The hook object is rebuilt every render; its refresh is a useCallback keyed
  // on dealId, so depend on that and keep handleChange's identity stable.
  const refreshDealFleetAndRoster = dealFleetAndRoster.refresh;
  const handleChange = useCallback(() => {
    if (typeof onChange === 'function') onChange();
    refreshDeal();
    // A roster or fleet write has to land on the deal-scoped panels too — the
    // account refresh above no longer feeds them.
    refreshDealFleetAndRoster();
  }, [onChange, refreshDeal, refreshDealFleetAndRoster]);

  const resolve = data?.resolve || null;
  // DEAL-scoped, NOT data.fleetAndRoster (which unions every deal on the
  // account). The union tagged a pilot bound on a sibling term as a current
  // pilot here — Alvin Arriola, bound on N802GA, on the N514TB workspace.
  // Everything downstream (roster, fleet, rail, fact strip, cockpit, markets)
  // reads this one value, so they are all deal-scoped together.
  const fleetAndRoster = dealFleetAndRoster.data || null;
  // Canonical per-term aircraft coverage (Supabase policy_aircraft). Separate
  // from fleetAndRoster, which is CRM-pipeline-derived — the two stores drift,
  // and the fleet panel must show/edit the one the carrier request reads.
  const fleetCoverage = useFleetCoverage(dealId);
  const accountId = resolve?.account_id || resolve?.id || resolve?.account?.id || null;
  // §5.2 lineage line: read off the account terms chain (one reader, the
  // /terms endpoint), never derived here.
  const [termsChain, setTermsChain] = useState(null);
  useEffect(() => {
    if (!accountId) { setTermsChain(null); return; }
    let alive = true;
    getAccountTerms(accountId)
      .then((r) => { if (alive) setTermsChain([...(r?.current || []), ...(r?.historical || [])]); })
      .catch(() => { if (alive) setTermsChain(null); });
    return () => { alive = false; };
  }, [accountId, dealId]);
  const lineage = (() => {
    if (!termsChain || !dealId) return null;
    const byId = new Map(termsChain.map((t) => [String(t.deal_id), t]));
    const me = byId.get(String(dealId));
    if (!me) return null;
    // 1e (2026-09-23): on a single-term account the line would read
    // "Term 1 · First" and say nothing the page does not already; hide it
    // unless the term followed something or the carrier moved. The chain on
    // the client record keeps its kind badge; only the header line is quiet.
    if (me.ordinal === 1 && me.kind === 'first' && !me.follows && !me.carrier_moved_from) return null;
    const parts = [];
    if (me.ordinal) parts.push(`Term ${me.ordinal}`);
    if (me.kind && KIND_LABELS[me.kind]) parts.push(KIND_LABELS[me.kind]);
    const prior = me.follows ? byId.get(String(me.follows)) || null : null;
    if (me.follows) parts.push('followed');
    const tail = [];
    if (me.carrier_moved_from) tail.push(`moved ${me.carrier_moved_from} \u2192 ${me.carrier}`);
    if (!parts.length && !tail.length) return null;
    return { text: parts.join(' \u00b7 '), prior, tail: tail.join(' \u00b7 ') };
  })();

  // Clear the optimistic stage once the refetched deal reflects the move (or
  // any other stage change lands).
  useEffect(() => { setPendingStage(null); }, [deal?.deal_stage]);

  // Zones a scroll door has asked to open. A DormantStrip reads its own id here.
  const [forcedZones, setForcedZones] = useState(() => new Set());

  // Scroll doors for cross-zone jumps (used by the cockpit + queue items).
  //
  // 199: THE `?.` IS GONE. `getElementById(id)?.scrollIntoView()` is what turned
  // a missing anchor into a control that does nothing AND REPORTS NOTHING: the
  // optional chain swallowed the miss, the click looked like it worked, and the
  // only way to find out was David going looking for the zone mid-bind.
  // A door whose target is missing says so, on the surface, not to the console.
  const scrollTo = useCallback((elId) => {
    setForcedZones((prev) => (prev.has(elId) ? prev : new Set(prev).add(elId)));
    requestAnimationFrame(() => {
      const el = document.getElementById(elId);
      if (!el) {
        toast.error(`Could not open "${elId}": that zone is not on this screen.`);
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);
  const openDocsZone = useCallback(() => scrollTo('zone-paper'), [scrollTo]);
  const openTrustFold = useCallback(() => scrollTo('trust-strip'), [scrollTo]);
  const openCockpit = useCallback(() => scrollTo('cockpit-workbench'), [scrollTo]);

  // Queue-item doors that resolve on THIS workspace (zone 2): open the
  // matching sheet in place instead of navigating. Returns true when handled.
  const handleZoneDoor = useCallback((door, item) => {
    if (door === 'quotes') { openCockpit(); return true; }
    if (door === 'review_quote' && item?.refs?.quote_id) { setReviewQuoteId(item.refs.quote_id); return true; }
    if (actionState.openers[door]) { actionState.openers[door](); return true; }
    return false;
  // setSearchParams is deliberately NOT in this list: it is declared BELOW this
  // callback, so naming it here is a temporal-dead-zone ReferenceError that
  // blanks the whole workspace. The body has always closed over it.
  }, [actionState, openCockpit, scrollTo]);

  // ?door= deep link (Ship 4a): a queue click lands on the exact door, not a
  // generic page. Any opener key works (paperwork, rtq, resume_link, ...);
  // 'quotes' scrolls to the cockpit, 'present' opens the present sheet.
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The experience selection (pilot_experience_unmatched): rows and aircraft ride
  // in from the refusal as router state, never re-derived here.
  const [experienceSelection, setExperienceSelection] = useState(null);
  const [answerPhf, setAnswerPhf] = useState(null);
  const doorFiredRef = useRef(null);
  useEffect(() => {
    const door = searchParams.get('door');
    if (!door || doorFiredRef.current === `${dealId}:${door}`) return;
    doorFiredRef.current = `${dealId}:${door}`;
    if (door === 'experience_model') {
      const sel = location.state?.experienceSelection;
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('door'); n.delete('phf'); return n; }, { replace: true });
      if (sel && Array.isArray(sel.rows)) setExperienceSelection(sel);
      else toast.info('Send to the carrier again to list the rows to name');
      doorFiredRef.current = null;  // a later refusal opens it again
      return;
    }
    // 'pilot_answer' (&phf=): a blocker about a pilot's answer opens that pilot's form, where each answer is the edit
    // (David 2026-11-04: one click on the answer itself, the pre-submit dialog included).
    if (door === 'pilot_answer') {
      const phf = searchParams.get('phf');
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('door'); n.delete('phf'); n.delete('field'); return n; }, { replace: true });
      if (phf) setAnswerPhf(phf); else toast.info('No pilot form on file to edit');
      doorFiredRef.current = null;
      return;
    }
    if (door === 'present') setPresentOpen(true);
    else if (door === 'quotes') openCockpit();
    // 'outcome' opens the stage popover that holds the manual move (Lost, Cancelled, Expired, a reason required): the one door that ends a deal.
    else if (door === 'outcome') setWhyOpen(true);
    // 'fleet' is not a sheet: the aircraft coverage surface is a panel on the
    // deal card view. A readiness blocker about a base airport or a runway is
    // fixed there, so clear the markets focus (the panel is not in that pane)
    // and scroll to it.
    else if (door === 'fleet') {
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('focus'); return n; }, { replace: true });
      // 200 §1: routed through THE scroll door. This was the last surviving
      // optional-chained scroll target after 199 took the other two off the
      // money and paper doors; a missing anchor here did nothing and reported
      // nothing, same as those did.
      setTimeout(() => scrollTo('deal-fleet'), 120);
    }
    // 'runway' (with &tail=): a base airport's runway, or the base airport
    // itself, is fixed in that aircraft's coverage dialog. Scroll to the fleet
    // and hand the tail to FleetPanel (?edit_coverage), which opens it.
    else if (door === 'runway') {
      const tail = searchParams.get('tail');
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('focus'); n.delete('tail'); if (tail) n.set('edit_coverage', tail); return n; }, { replace: true });
      setTimeout(() => scrollTo('deal-fleet'), 120);
    }
    // 'money' is a zone, not a sheet, like 'fleet'. A deferred collection from
    // the waiting set lands on the money strip, where the Deferred tile and the
    // broker's own reason already render.
    else if (door === 'money') {
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('focus'); return n; }, { replace: true });
      // 199: routed through THE scroll door rather than repeating it inline with
      // its own `?.`. One derivation, one place the miss is reported.
      setTimeout(() => scrollTo('zone-money'), 120);
    }
    // 'roster' is a panel, not a sheet, like 'fleet'. A pilot_unlinked queue
    // item lands on the roster panel, whose holding strip carries Confirm.
    else if (door === 'roster') {
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('focus'); return n; }, { replace: true });
      setTimeout(() => scrollTo('deal-roster'), 120);
    }
    else if (actionState.openers[door]) actionState.openers[door]();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('door');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, dealId, actionState, openCockpit]);

  // Cockpit V-B4 rail wiring (Ship 1.2). The markets quickview clicks into the
  // markets surface (Ship 1.3 upgrades this to a main-pane focus swap; for now
  // it scrolls to the ladder). A needs-you item resolves on this workspace when
  // its door is a local sheet, else it navigates (mirrors DealQueuePanel.fire).
  // Focus swap (Ship 1.3): ?focus=markets replaces the main pane with the
  // markets work surface; the rail stays put. Note there was no prior ?focus
  // convention in this app (the existing deep-link param is ?door=); ?focus is
  // introduced here and coexists with ?door.
  const openMarketsFocus = useCallback(() => {
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('focus', 'markets'); return n; });
  }, [setSearchParams]);
  const unfocus = useCallback(() => {
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('focus'); return n; });
  }, [setSearchParams]);
  const handleNeedsAction = useCallback((item) => {
    // A market-waiting signal focuses the markets surface (mockup: a needs-you
    // market item opens the ladder); everything else routes by its door.
    if (item.kind === 'market_waiting') { openMarketsFocus(); return; }
    const local = NEEDS_LOCAL_DOOR[item.kind];
    if (local && handleZoneDoor(local, item)) return;
    const door = doorForItem(item);
    if (door) navigate(door.href);
  }, [handleZoneDoor, navigate, openMarketsFocus]);

  // Feed (or clear) the shell right rail with the cockpit rail. Re-runs on
  // deal-data refresh (refreshKey) so the rail restages; clears on unmount so
  // list views go back to two columns.
  // Door collapse (Ship 3): on an aviation deal the "Log quote" / "Enter a quote"
  // verbs route into the markets ladder (quick-log new, Quote detail existing)
  // instead of the old QuoteEntrySheet. Every other line keeps the sheet.
  // An item that opens a sheet keeps focus in the sheet: the menu's closing animation ends after the sheet is open, and its
  // focus back to "More actions" made the sheet's focus trap select the field being typed in, so the next key replaced it
  // (David 2026-11-03). The aviation quote verb scrolls the markets ladder and keeps the default.
  const menuOpenedSheet = useRef(false);
  const actionOpensSheet = (key) => !(key === 'quote' && isAviation) && !!actionState.openers[key];
  const keepFocusInSheet = (e) => { if (menuOpenedSheet.current) { menuOpenedSheet.current = false; e.preventDefault(); } };
  const invokeAction = useCallback((key) => {
    if (key === 'quote' && isAviation) { openMarketsFocus(); return; }
    actionState.openers[key]?.();
  }, [isAviation, openMarketsFocus, actionState]);
  const enterQuote = isAviation ? openMarketsFocus : actionState.openers.quote;
  const composeClient = actionState.openers.send;
  useEffect(() => {
    if (railOpen) {
      setRail(
        <DealCockpitRail
          dealId={dealId}
          accountId={accountId}
          refreshKey={data}
          onFocusMarkets={openMarketsFocus}
          onEnterQuote={enterQuote}
          onComposeClient={composeClient}
          onNeedsAction={handleNeedsAction}
        />
      );
    } else {
      setRail(null);
    }
    return () => clearRail();
  }, [railOpen, dealId, accountId, data, openMarketsFocus, enterQuote, composeClient, handleNeedsAction, setRail, clearRail]);

  if (!data) return null;

  const focus = searchParams.get('focus');
  const insuredName = resolve?.account_name || deal?.named_insured || 'Deal';
  const title = deal?.deal_name || insuredName;
  const showInsuredBeside = Boolean(deal?.deal_name && deal.deal_name !== insuredName);
  const stage = deal?.stage || null;
  const stageLabel = deal?.deal_stage || insuranceLine || 'Deal';
  // Derived lifecycle state (server-computed, DEAL-LIFECYCLE-SPEC Phase 1).
  // The badge renders it when present; the derived pipeline stage is the
  // fallback for payloads that predate the state computation.
  const lifecycle = deal?.lifecycle_state || null;
  async function handleTerminalMove() {
    const newStage = reasonStage;
    const reason = reasonText.trim();
    if (!newStage || !dealId || !reason) return;
    setReasonStage(null);
    setReasonText('');
    setReasonBecause(null);
    setWhyOpen(false);
    setPendingStage(newStage.toLowerCase());
    try {
      await updateDealStage(dealId, newStage, { reason, ...(reasonBecause ? { ended_because: reasonBecause } : {}) });
      toast.success(reasonBecause ? `Marked ${ENDED_BECAUSE_WORDS[reasonBecause] || newStage}` : `Marked ${newStage}`);
      handleChange();
    } catch (err) {
      setPendingStage(null);
      const msg = err?.body?.error || err?.code || err?.message || 'failed';
      toast.error(`Stage change failed: ${msg}`);
    }
  }
  const effective =
    formatHumanDate(deal?.proposed_effective) ||
    formatHumanDate(deal?.policy_start) ||
    formatHumanDate(deal?.policy_expiration);
  const acCount = deal?.aircraft?.length ?? (fleetAndRoster?.fleet?.length || 0);
  const pilotCount = deal?.pilots?.total ?? (fleetAndRoster?.roster?.length || 0);
  const quoteCount = deal?.quotes?.total ?? 0;
  const docCount = data?.documents?.total_files ?? 0;
  const lastActivity = deal?.days_since_last_activity;

  const ctx = { dealId, deal, fleetAndRoster, fleetCoverage, onCoverageSaved: fleetCoverage.refresh, onChange: handleChange, commercial: isCommercial ? commercial : null, documents: data?.documents || null, accountId, onOpenDocs: openDocsZone, onOpenTrust: openTrustFold, lob: line.isProperty ? 'hangar' : 'hull_liability' };

  // Header META (Ship 4 item 4): a property record shows property facts —
  // jobsite, completed value, BR status — never aircraft/pilot/quote counts.
  const brProject = isBuildersRisk ? parseBrData(deal?.Builders_Risk_Data)?.project : null;
  const jobsiteCityState = brProject?.jobsite_address
    ? [brProject.jobsite_address.city, brProject.jobsite_address.state].filter(Boolean).join(', ')
    : null;
  const lockKey = effective && deal?.effective_lock
    ? <EffectiveLockKey dealId={dealId} lock={deal.effective_lock} policyStart={deal?.policy_start} onChange={handleChange} />
    : null;
  const feeKey = deal?.fees
    ? <DealFeesKey dealId={dealId} fees={deal.fees} onChange={handleChange} />
    : null;
  // The policy's producer (deals-door-1 T16): the picker for the owner's own sign-in, the name or "Not stated" for others.
  const producerKey = dealId ? <DealProducerKey dealId={dealId} /> : null;
  // The policy number (2026-10-30): wherever the deal's own action list offers the policy-info door.
  const policyNumberKey = dealId && actionState.actions.some(({ key }) => key === 'policy_info')
    ? <DealPolicyNumberKey dealId={dealId} policyNumber={deal?.policy_number || ''} onChange={handleChange} />
    : null;
  const META = (isBuildersRisk
    ? [
        effective ? `Property · eff ${effective}` : 'Property',
        producerKey,
        jobsiteCityState,
        brProject?.completed_value != null ? `${formatCurrency(brProject.completed_value)} completed value` : null,
        brStatusWord(deal?.BR_Status),
        lastActivity != null ? `Last activity ${lastActivity === 0 ? 'today' : `${lastActivity}d ago`}` : null,
      ]
    : isHangar
    ? [
        // Occupied hangars are line-true: no fleet or roster counts, they
        // are structurally zero on a property record.
        effective ? `${line.display} · eff ${effective}` : line.display,
        policyNumberKey,
        lockKey,
        feeKey,
        producerKey,
        quoteCount ? `${quoteCount} quote${quoteCount === 1 ? '' : 's'}` : null,
        docCount ? `${docCount} file${docCount === 1 ? '' : 's'}` : null,
        lastActivity != null ? `Last activity ${lastActivity === 0 ? 'today' : `${lastActivity}d ago`}` : null,
      ]
    : [
        insuranceLine && (effective ? `${insuranceLine} · eff ${effective}` : insuranceLine),
        policyNumberKey,
        lockKey,
        feeKey,
        producerKey,
        acCount || pilotCount ? `${acCount} aircraft · ${pilotCount} pilot${pilotCount === 1 ? '' : 's'}` : null,
        quoteCount ? `${quoteCount} quote${quoteCount === 1 ? '' : 's'}` : null,
        docCount ? `${docCount} file${docCount === 1 ? '' : 's'}` : null,
        lastActivity != null ? `Last activity ${lastActivity === 0 ? 'today' : `${lastActivity}d ago`}` : null,
      ]
  ).filter(Boolean);

  // THE one primary action for the current derived state (zone 1). Falls
  // back to the legacy emphasis set when the state has no ruled primary.
  const visibleActions = actionState.actions.filter(({ key }) => !(isProperty && PROPERTY_HIDDEN_ACTIONS.has(key)));
  const primaryAction = primaryActionFor(actionState.lifecycleState, visibleActions)
    || visibleActions.find(({ key }) => PRIMARY_KEYS.has(key))
    || null;

  // Pre-bind = derived state before "bound" ("Bound means paid"). While pre-bind
  // the money and paperwork zones carry nothing, so they collapse to one-line
  // strips at the bottom of the column (never hidden; expandable). This also
  // lets the submission machinery sit higher up the page. Uses the derived
  // lifecycle state, not a CRM stage read.
  //
  // 196 §2: `binding` came OUT of this list. A deal being actively bound is
  // exactly when the broker is working paper, so starting the paperwork zone
  // collapsed there is what made it feel missing. The other three carry
  // genuinely nothing and keep the collapsed default.
  const isPreBind = ['prospect', 'shopping', 'presented'].includes(actionState.lifecycleState);

  // The bright header stage word (SHOP/PRESENT/BIND/PAPER/SERVICE); terminal
  // states fall back to the muted StageBadge inside the popover trigger.
  const stageTag = stageWord(pendingStage || lifecycle?.state || actionState.lifecycleState);

  // Every other verb folds into the header overflow menu (the bottom action
  // pile is gone). Excludes the accent primary (rendered separately) and the
  // property-hidden aviation doors. The binding filter went with the binding
  // action itself (193 §4) — it guarded a key that no longer exists.
  const overflowActions = actionState.actions.filter(({ key }) => {
    if (primaryAction && key === primaryAction.key) return false;
    if (isProperty && PROPERTY_HIDDEN_ACTIONS.has(key)) return false;
    return true;
  });

  // Main-pane entity cards per line (Cockpit V-B4 1.1). Aviation is the
  // mockup composition (aircraft, pilots, coverage targets); other lines keep
  // their existing line-specific panels within the same shell. Named insured
  // and additional interests bracket this list on every line.
  let mainPanelNodes;
  if (isCommercial) {
    mainPanelNodes = [renderPanel('CommercialCoveragePanel', ctx), renderPanel('CommercialOperationsPanel', ctx)];
  } else if (isBuildersRisk) {
    mainPanelNodes = [renderPanel('BrProjectPanel', ctx)];
  } else if (isHangar) {
    mainPanelNodes = [renderPanel('HangarAnnualReviewPanel', ctx), renderPanel('SubmissionPanel', ctx), renderPanel('HangarLocationsPanel', ctx)];
  } else {
    mainPanelNodes = [
      renderPanel('FleetPanel', ctx),
      renderPanel('RosterPanel', ctx),
      <CoverageTargetsCard key="coverage" deal={deal} onEdit={actionState.openers.edit_roster} />,
    ];
  }
  // The line quoting workbench folds one layer down (aviation only; the other
  // lines surface their cockpit panel as a main card already).
  const cockpitNodes = isAviation ? [renderPanel('SubmissionBuilderPanel', ctx)] : [];

  // ── SERVICING DOORS, DEFINED ONCE, RENDERED IN EVERY FOCUS (190 §1) ───────
  //
  // The main pane is a ternary on `focus === 'markets'`. That branch rendered
  // the ladder and NOTHING ELSE, so Additional Interests and the whole Policy
  // paperwork zone were not on the screen at all -- not collapsed, not moved,
  // not rendered. And the app focuses markets on its own when a market-waiting
  // signal fires, so the broker can land there without choosing it and find no
  // door to the paperwork.
  //
  // David: "it MUST always serve as a midterm servicing... and i don't seem to
  // have that view any longer." An additional interest missing from a binding
  // application is NORMAL. The binding ceremony is not the path; midterm
  // servicing is, and it has to work whether or not the client typed anything.
  //
  // DEFINED ONCE AND RENDERED TWICE, never pasted: one definition is what stops
  // the two focus states drifting into two different paperwork zones. Only one
  // branch of the ternary mounts at a time, so the DOM ids stay unique and the
  // existing #zone-paper / #zone-money deep links keep working in both.
  const interestsNode = (
    <AdditionalInterestsPanel dealId={dealId} accountId={accountId} onChanged={handleChange} />
  );
  // The pre-bind DormantStrip default is UNCHANGED and deliberately so: whether
  // paperwork should start collapsed before bind is David's call, not a thing
  // to alter while restoring a missing door (190 §1.2). This mirrors it rather
  // than deciding it.
  // The client's pick sits OUTSIDE the dormant money strip: pre-bind is exactly
  // when it goes stale and has to be seen. Renders nothing until a pick exists.
  const clientPickNode = <ClientPickCard dealId={dealId} refreshKey={dealData} onChanged={handleChange} />;
  const moneyAndPaperNodes = !isPreBind ? (
    <>
      {clientPickNode}
      <div id="zone-money" className="scroll-mt-24">
        <MoneyStrip dealId={dealId} refreshKey={dealData} onReversed={handleChange} />
      </div>
      <div id="zone-paper" className="scroll-mt-24">
        <PaperworkTab dealId={dealId} accountId={accountId} documents={data.documents} onChange={handleChange}
          onReviewPaperwork={actionState.openers.paperwork} onOpenTrust={openTrustFold} />
      </div>
    </>
  ) : (
    <>
      {clientPickNode}
      {/* 199: the id is on the STRIP, not on the children, so the anchor exists
          whether the strip is open or closed. Collapsed pre-bind stays: there
          is no paper at these stages. */}
      <DormantStrip id="zone-money" title="Money" forceOpen={forcedZones.has('zone-money')}>
        <MoneyStrip dealId={dealId} refreshKey={dealData} onReversed={handleChange} />
      </DormantStrip>
      <DormantStrip id="zone-paper" title="Policy paperwork" forceOpen={forcedZones.has('zone-paper')}>
        <PaperworkTab dealId={dealId} accountId={accountId} documents={data.documents} onChange={handleChange}
          onReviewPaperwork={actionState.openers.paperwork} onOpenTrust={openTrustFold} />
      </DormantStrip>
    </>
  );

  return (
    <div
      data-deal-workspace
      className="relative flex min-h-full flex-col"
      onDragEnter={(e) => {
        if ([...(e.dataTransfer?.types || [])].includes('Files')) {
          e.preventDefault();
          setDragDepth((d) => d + 1);
        }
      }}
      onDragOver={(e) => {
        if ([...(e.dataTransfer?.types || [])].includes('Files')) e.preventDefault();
      }}
      onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
      onDropCapture={(e) => {
        // Capture phase clears the hover overlay for EVERY drop, including
        // drops a declared zone claims with stopPropagation.
        if ([...(e.dataTransfer?.types || [])].includes('Files')) setDragDepth(0);
      }}
      onDrop={(e) => {
        // Prompt 173 §2b: the open-workspace fallback ASKS. It routes nothing
        // by itself; declared zones (interest cert rows, the doc sheets)
        // claim their drops before this fires.
        if ([...(e.dataTransfer?.types || [])].includes('Files')) {
          e.preventDefault();
          openDropRouter(e.dataTransfer.files);
        }
      }}
    >
      {/* 198 §1.1: THE PAGE IS NOT THE TARGET, THE ZONE IS.
          This was a full-screen scrim (`inset-0` + backdrop-blur) that covered
          every declared drop zone at the exact moment the broker needed to see
          which one would catch the file. It is now a banner pinned to the
          bottom, so the zones underneath stay visible and can show their own
          highlight, and the page reads as the FALLBACK it is.

          STATED LIMIT, because §1.2 cannot be fully honoured: the browser does
          not expose filenames during a drag. `dataTransfer.files` is empty and
          `items[i].getAsFile()` returns null until the drop fires, so nothing
          can name the TYPE it would file as while the file is still in the air.
          Naming a type here would mean guessing without the one input the
          guesser uses. It names the DESTINATION and says it will ask, which is
          what is actually knowable. */}
      {(dragDepth > 0 || parsingQuote) && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-40 flex justify-center p-4">
          <div className="rounded-lg border-2 border-dashed border-accent bg-bg/95 px-6 py-3 text-center shadow-lg">
            <p className="text-[14px] font-semibold text-text">
              {parsingQuote ? 'Parsing quote…' : 'Drop anywhere on this deal'}
            </p>
            <p className="mt-0.5 text-[12px] text-text-muted">
              {parsingQuote
                ? 'Reading terms and options; the review screen opens next'
                : 'A highlighted zone will claim it. Otherwise TRACON asks what it is, and nothing is written until you confirm.'}
            </p>
          </div>
        </div>
      )}
      {/* Drop router (prompt 173 §2b; paperwork-1 screen 2): the answer routes the file. A
          preselected suggestion is a default, never a determination. */}
      <Dialog open={dropOpen} onOpenChange={(v) => { if (!v && !routingDrop) closeDropRouter(); }}>
        <DialogContent className="max-w-2xl" data-drop-dialog>
          <DialogHeader>
            <DialogTitle>{drop.entries.length === 1 ? 'What is this file?' : 'What are these files?'}</DialogTitle>
            <DialogDescription>Files each document on this policy under the kind you choose. Your answer routes each file. Nothing is written until you confirm.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <DocumentDropRows drop={drop} disabled={routingDrop} />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={routingDrop} onClick={closeDropRouter}>{dropPending ? 'Cancel' : 'Done'}</Button>
            {dropPending ? (
              <Button variant="accent" disabled={routingDrop || !drop.ready} onClick={routeDroppedFiles}>
                {routingDrop ? 'Filing…' : dropPending === 1 ? 'File it' : `File ${dropPending} files`}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <input
        ref={quoteFileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          handleQuoteFile(e.target.files?.[0]).catch(() => {}); // said once by handleQuoteFile's toast
          e.target.value = '';
        }}
      />
      {/* DEAL HEADER */}
      <header className="sticky top-0 z-10 border-b border-border bg-bg/95 px-4 pt-4 pb-3 backdrop-blur md:px-6">
        <nav className="mb-2 flex items-center gap-1.5 text-[11px] text-text-dim">
          <Link to="/" className="hover:text-text">Queue</Link>
          <span className="opacity-50">/</span>
          <span>{[insuranceLine, deal?.deal_stage].filter(Boolean).join(' · ') || 'Deal'}</span>
          <span className="opacity-50">/</span>
          <span className="text-text-muted">{insuredName}</span>
        </nav>

        <div className="mb-2.5 flex items-center gap-3.5">
          {/* Deal_Name is the working identity on multi-deal accounts (Ship 4
              item 9: "PUJ Hangar 670", not the sixth "LH Construction" row) —
              it titles the workspace on every line, with the insured name
              beside it when the two differ. */}
          <h1 className="truncate text-[19px] font-semibold tracking-tight text-cream">{title}</h1>
          <TestBadge isTest={deal && typeof deal.is_test !== 'undefined' ? deal.is_test : resolve?.is_test} />
          {showInsuredBeside ? (
            <span className="hidden truncate text-[13px] text-text-muted sm:inline">{insuredName}</span>
          ) : null}
          {/* Stage badge "why" popover (DEAL-LIFECYCLE-SPEC Phase 1): the
              badge explains — click shows the facts the state derives from.
              Manual moves are terminal-only (Lost/Cancelled/Expired) with a
              typed reason; there is no force-stage door. */}
          {(pendingStage || lifecycle?.state || stage || stageLabel) ? (
            <Popover open={whyOpen} onOpenChange={setWhyOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  title="Why this state?"
                  className="inline-flex items-center gap-1 rounded-card hover:opacity-80"
                >
                  {stageTag.word ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/45 bg-accent-soft px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.06em] text-accent">
                      {stageTag.word}
                    </span>
                  ) : (
                    <StageBadge stage={pendingStage || lifecycle?.state || stage || stageLabel} />
                  )}
                  {lifecycle?.legacy ? (
                    <span className="rounded border border-border px-1 text-[9px] uppercase tracking-wide text-text-dim">legacy</span>
                  ) : null}
                  <ChevronDown size={14} strokeWidth={1.5} className="text-text-dim" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" side="bottom" sideOffset={4} className="w-80 p-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                  Why this state
                </div>
                {(lifecycle?.facts || []).length ? (
                  <ul className="space-y-1.5">
                    {lifecycle.facts.map((f, i) => (
                      <li key={i} className="text-[12.5px] leading-snug">
                        <span className="font-medium text-text">{f.label}</span>
                        {f.detail ? <span className="text-text-muted"> · {f.detail}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[12.5px] text-text-muted">
                    State derives from the ledger, quotes, and sessions. Reload the deal to compute it.
                  </p>
                )}
                <div className="mt-3 border-t border-border pt-2">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    Manual move (reason required)
                  </div>
                  <div className="flex gap-1.5">
                    {TERMINAL_MOVES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { setReasonText(''); setReasonBecause(null); setReasonStage(s); }}
                        className="rounded border border-border px-2 py-1 text-[11.5px] text-text-muted hover:border-border-strong hover:text-text"
                      >
                        {s}
                      </button>
                    ))}
                    {BECAUSE_MOVES.map((m) => (
                      <button
                        key={m.because}
                        type="button"
                        onClick={() => { setReasonText(''); setReasonBecause(m.because); setReasonStage(m.stage); }}
                        className="rounded border border-border px-2 py-1 text-[11.5px] text-text-muted hover:border-border-strong hover:text-text"
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : null}
          {/* Readiness (one tag, WALKTHROUGH fix 3) relocated into the header
              beside the stage word (Cockpit V-B4 1.1). Self-hides when the
              deal has no intake session. */}
          {!isProperty ? <IntakeReadinessCard dealId={dealId} refreshKey={data} openers={actionState.openers} accountId={accountId} /> : null}
          {deal?.pending_paper_drafts > 0 ? (
            <button
              type="button"
              onClick={actionState.openers.paperwork}
              className="inline-flex items-center gap-1 rounded-full border border-warning/50 bg-warning/10 px-2.5 py-0.5 text-[11px] font-medium text-warning hover:bg-warning/20"
            >
              Paperwork ready to review ({deal.pending_paper_drafts})
            </button>
          ) : null}
          {/* READY TO BIND (David 2026-08-12): warn, never block. The server
              attaches bind_readiness only on binding/bound states, and only
              when it could actually read the deal — so absence here means
              "nothing to warn about or nothing was checked", never a gate. */}
          {deal?.bind_readiness?.missing?.length ? (
            <span
              title={deal.bind_readiness.missing.map((m) => m.message).join('\n')}
              className="inline-flex items-center gap-1 rounded-full border border-warning/50 bg-warning/10 px-2.5 py-0.5 text-[11px] font-medium text-warning"
            >
              Bind gaps ({deal.bind_readiness.missing.length})
            </span>
          ) : null}
          {/* PILOT FORMS BEFORE A RENEWAL (PILOT-ATTESTATION.md, step 2): from 30
              days before Policy_Start the server names the pilots the signing
              lock waits on, so the nudges have time. Warn, never block. */}
          {deal?.pilot_readiness && (deal.pilot_readiness.unknown || deal.pilot_readiness.outstanding?.length) ? (
            <span
              title={deal.pilot_readiness.unknown ? 'Pilot forms could not be checked just now' : deal.pilot_readiness.outstanding.map((o) => `${o.name}: ${o.reason === 'stale' ? `attested ${o.attested_ymd}, more than 12 months ago` : o.reason === 'no_form' ? 'no form on this application' : o.reason === 'unknown' ? 'could not be checked' : 'not attested'}`).join('\n')}
              className="inline-flex items-center gap-1 rounded-full border border-warning/50 bg-warning/10 px-2.5 py-0.5 text-[11px] font-medium text-warning"
            >
              {deal.pilot_readiness.unknown ? 'Pilot forms: not checked' : `Pilot forms outstanding (${deal.pilot_readiness.outstanding.length})`}
            </span>
          ) : null}
          <div className="ml-auto flex items-center gap-1.5">
            {/* THE one primary action for the current state (accent). Every
                other verb folds into the overflow menu (bottom action pile
                removed, Cockpit V-B4 1.1). */}
            {primaryAction ? (
              <button
                type="button"
                onClick={() => invokeAction(primaryAction.key)}
                className="inline-flex min-h-[32px] shrink-0 items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1 text-[12.5px] font-medium text-white hover:brightness-110"
              >
                <primaryAction.Icon size={14} strokeWidth={1.5} />
                {primaryAction.label}
              </button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title="More actions"
                  className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text"
                >
                  <MoreHorizontal size={17} strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" onCloseAutoFocus={keepFocusInSheet}>
                {overflowActions.map(({ key, label, Icon }) => (
                  <DropdownMenuItem key={key} onSelect={() => { menuOpenedSheet.current = actionOpensSheet(key); invokeAction(key); }} className="gap-2">
                    <Icon size={14} strokeWidth={1.5} className="text-text-muted" />
                    {label}
                  </DropdownMenuItem>
                ))}
                {!isProperty ? (
                  <>
                    <DropdownMenuItem onSelect={() => quoteFileInputRef.current?.click()} className="gap-2">
                      <FileUp size={14} strokeWidth={1.5} className="text-text-muted" />
                      Upload quote
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setPresentOpen(true)} className="gap-2">
                      <ExternalLink size={14} strokeWidth={1.5} className="text-text-muted" />
                      Present quotes
                    </DropdownMenuItem>
                  </>
                ) : null}
                {deal?.app_number ? (
                  <div className="border-t border-border px-2 py-1.5 text-[11px] text-text-dim">
                    {deal.app_number}
                  </div>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              title="Open in new tab"
              onClick={() => window.open(window.location.pathname, '_blank', 'noopener')}
              className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text"
            >
              <ExternalLink size={15} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              title={railOpen ? 'Hide details' : 'Show details'}
              onClick={() => setRailOpen((v) => !v)}
              className="hidden h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text lg:grid"
            >
              {railOpen ? <PanelRightClose size={15} strokeWidth={1.5} /> : <PanelRightOpen size={15} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
        {/* CLIENT-RECORD-SPEC §5.2: one line, which term of the chain this is
            and what it followed, linking up to the record. */}
        {lineage ? (
          <div className="mb-2.5 -mt-1 text-[11.5px] text-text-dim" data-lineage>
            {lineage.text}
            {lineage.prior?.href ? <> <Link to={lineage.prior.href} className="text-text-muted hover:text-text hover:underline">{lineage.prior.carrier || 'prior term'}{lineage.prior.policy_end ? ` ${String(lineage.prior.policy_end).slice(0, 4)}` : ''}</Link></> : null}
            {lineage.tail ? <> {'\u00b7'} {lineage.tail}</> : null}
            {accountId ? <> <span className="opacity-50">/</span> <Link to={`/account/${encodeURIComponent(accountId)}`} className="hover:text-text hover:underline">client record</Link></> : null}
          </div>
        ) : null}

        {META.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-text-muted">
            {META.map((m, i) => (
              <span key={i} className="flex items-center gap-2.5">
                {i > 0 && <span className="opacity-40">·</span>}
                {m}
              </span>
            ))}
          </div>
        )}
        {/* Reopen (Q1, 2026-10-28): an edge-case tool, quiet, only for a holder of the privilege on an ended deal. */}
        {dealId ? <DealReopenKey dealId={dealId} onReopened={handleChange} /> : null}
        {/* Round 2: a cancellation waiting on the signed LPR or the carrier's endorsement, in words. */}
        {dealId ? <DealCancellationNote dealId={dealId} /> : null}
      </header>

      {/* MAIN PANE (left column; the shell provides the 320px right rail). */}
      <section className="mx-auto w-full max-w-[1000px] flex-1 space-y-3 px-4 py-4 md:px-6 md:py-5">
        {/* Part B: a held out-of-state named-insured address change blocks the
            client's sign step until the broker approves or denies it here. */}
        {deal?.pending_address_change ? (
          <AddressChangePendingBanner
            dealId={dealId}
            pending={deal.pending_address_change}
            onResolved={handleChange}
          />
        ) : null}
        {/* MAIN PANE. In markets focus (Ship 1.3) it swaps to the markets work
            surface (fact strip + ladder) while the shell rail stays put;
            otherwise it is the V-B4 deal card stack (named insured, aircraft,
            pilots, coverage targets, additional interests, then the dormant
            strips). */}
        {focus === 'markets' ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={unfocus}
              className="inline-flex items-center gap-1 text-[12px] text-text-muted hover:text-text"
            >
              &larr; Back to deal
            </button>
            {isAviation ? (
              <>
                <MarketsFactStrip deal={deal} fleetAndRoster={fleetAndRoster} />
                {/* CC PROMPT 31 Part C5. Read-only: what the client is looking
                    at, answered without opening the sheet. The sheet stays the
                    editor and this never writes. Renders nothing until
                    something has been presented. */}
                <ClientPickCard dealId={dealId} refreshKey={data} onChanged={handleChange} />
                <PresentedStrip
                  dealId={dealId}
                  refreshKey={data}
                  onOpenEditor={() => setPresentOpen(true)}
                />
                <div id="markets-ladder" className="scroll-mt-24">
                  {/* No accountId: the ladder reads its quotes deal-scoped now
                      (prompt 67 item 2.2), so passing an account here would
                      imply a scope this surface no longer has. */}
                  <MarketsWorkSurface
                    dealId={dealId}
                    deal={deal}
                    fleetAndRoster={fleetAndRoster}
                    refreshKey={data}
                    onPresent={() => setPresentOpen(true)}
                    // "View submission intake" is HIDDEN, not removed. It opens
                    // SubmissionBuilderPanel — the second cockpit, which reads
                    // `submissions` / `submission_scenarios` while this surface
                    // writes `submission_markets`. That store has not been
                    // written since 2026-07-29, so on a current deal the panel
                    // opens empty and reads as a broken screen. Withholding the
                    // prop drops the button (MarketsWorkSurface renders it only
                    // when onViewIntake is passed) and leaves the dialog and the
                    // panel intact below, so restoring it is this one line.
                    // The starvation itself is NOT fixed — see
                    // COCKPIT-VB4-REWORK-DIFF.md.
                  />
                </div>
                {/* THE SERVICING DOORS, BELOW THE LADDER. Same definitions the
                    deal pane renders, so the two focus states cannot drift.
                    Below rather than behind navigation: the broker is here to
                    work the market, and the paperwork is what he reaches for
                    next without going anywhere. */}
                {interestsNode}
                {moneyAndPaperNodes}
              </>
            ) : (
              // Airport Property and Commercial deals have no aviation ladder;
              // the markets deep link lands on the line's own cockpit (the
              // ?focus=markets trap: the work surface is hard-coded aviation).
              <div id="markets-ladder" className="scroll-mt-24 space-y-3">
                {mainPanelNodes}
                {/* Property and Commercial deals land here through the same
                    ?focus=markets deep link and lost the same doors. */}
                {interestsNode}
                {moneyAndPaperNodes}
              </div>
            )}
          </div>
        ) : (
        <div id="deal-pane" className="space-y-3">
          <NamedInsuredCard resolve={resolve} deal={deal} onEdit={actionState.openers.edit_insured} />

          {mainPanelNodes}

          {interestsNode}

          {(deal?.Builders_Risk_Data || deal?.BR_Status) && !isCommercial ? (
            <BrStatusPanel item={deal} dealId={dealId} onChange={handleChange} onIssueCoi={actionState.openers.coi} />
          ) : null}
          {/* OPEN-FINDINGS #19: CaRequestDialog was reachable ONLY through the
              top-level `binding` action, which 193 §4 removed, so Commercial
              Aviation lost its binding door entirely while Builders Risk kept
              its own. Mounted the same way BrStatusPanel is, and NOT gated on
              CA_Status: the panel's whole job in `not_sent` is to offer the
              send, so gating on the status would only show the door after it
              had already been used. */}
          {isCommercial ? (
            <CaStatusPanel item={deal} dealId={dealId} onChange={handleChange} />
          ) : null}
          {isBuildersRisk ? <BrMarketQuotesPanel dealId={dealId} /> : null}
          {/* Needs-you lives in the shell rail at lg+; on phone (no rail) the
              deal keeps its inline what's-waiting queue. */}
          <div className="lg:hidden">
            <DealQueuePanel dealId={dealId} refreshKey={dealData} onDoor={handleZoneDoor} />
          </div>
          {/* MAKE-MODEL-TIME-SPEC section 3: "Place this model" (and the data plate) while a question is open. */}
          <DealModelsPanel dealId={dealId} refreshKey={dealData} />

          {/* Money + paperwork: live cards post-bind; dormant strips pre-bind
              (nothing there until binding). */}
          {moneyAndPaperNodes}

          {/* Machinery one layer down: quoting workbench + trust, reachable
              but out of the way (mockup keeps the card stack clean). */}
          {cockpitNodes.length > 0 ? (
            <DormantStrip id="cockpit-workbench" title="Quoting workbench" closedLabel="expand">
              {cockpitNodes}
            </DormantStrip>
          ) : null}

          <DormantStrip title="Comms timeline" closedLabel="expand">
            <CommsTimelinePanel timeline={data.commsTimeline} dealId={dealId} accountId={accountId} onComposed={onChange} />
          </DormantStrip>

          <DormantStrip id="trust-strip" title="Trust and transactions" closedLabel="expand">
            <TrustLedgerPanel ledger={data.trustLedger} />
            <TransactionHistoryPanel dealId={dealId} clientName={resolve?.account_name || deal?.named_insured || null} />
          </DormantStrip>
        </div>
        )}
      </section>

      {/* All per-deal sheets/modals, wired to the shared action state. onChange
          is handleChange so a present/save also refreshes THIS view's deal
          header (stage + client-visible), not just the parent account data
          (item 7c). */}
      <DealActionsSheets dealId={dealId} actionState={actionState} onChange={handleChange} />

      {/* MV-03 review screen: source left, extracted fields right, approve
          writes the quote (draft → received). */}
      <QuoteParseReviewSheet
        quoteId={reviewQuoteId}
        open={!!reviewQuoteId}
        onOpenChange={(o) => { if (!o) setReviewQuoteId(null); }}
        onResolved={handleChange}
      />

      <PhfSubmissionSheet phfId={answerPhf} pilotName="" open={!!answerPhf} onOpenChange={(o) => { if (!o) { setAnswerPhf(null); handleChange(); } }} />
      <ExperienceModelDialog
        selection={experienceSelection}
        open={!!experienceSelection}
        onOpenChange={(o) => { if (!o) setExperienceSelection(null); }}
      />

      {/* MV-03 present flow: curated options + one recommended + channels. */}
      <PresentQuotesSheet
        dealId={dealId}
        open={presentOpen}
        onOpenChange={setPresentOpen}
        onPresented={handleChange}
        onReopened={handleChange}
      />

      {/* Ship 2.1: "View submission intake" from the markets work surface opens
          the existing submission view (the as-applied-for spine) in a dialog so
          the broker never leaves the markets focus to check what was applied. */}
      {isAviation ? (
        <Dialog open={intakeViewOpen} onOpenChange={setIntakeViewOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Submission intake</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] overflow-y-auto">
              <SubmissionBuilderPanel
                dealId={dealId}
                fleetAndRoster={fleetAndRoster}
                documents={data?.documents || null}
                accountId={accountId}
                onChange={handleChange}
                onOpenDocs={openDocsZone}
                onOpenTrust={openTrustFold}
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Terminal-move reason dialog: Lost / Cancelled / Expired require a
          typed reason (DEAL-LIFECYCLE-SPEC Phase 1). */}
      <Dialog open={!!reasonStage} onOpenChange={(o) => { if (!o) { setReasonStage(null); setReasonBecause(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark {reasonBecause ? ENDED_BECAUSE_WORDS[reasonBecause] : reasonStage}?</DialogTitle>
            <DialogDescription>
              {reasonBecause === 'aircraft_sold'
                ? 'The term ended and was not renewed because the aircraft was sold. The deal is marked Lost, not renewed, with the reason aircraft sold, so reports count the sale. A sale mid-term is a cancellation: start the LPR and choose Aircraft sold as its reason. Needs your own Google sign-in.'
                : 'This is a manual terminal move. Say why so the record explains itself later.'}
            </DialogDescription>
          </DialogHeader>
          <input
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder={reasonBecause === 'aircraft_sold' ? 'e.g. sold to the new owner, closing date' : reasonStage === 'Lost' ? 'e.g. client went with another broker' : 'reason'}
            className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReasonStage(null); setReasonBecause(null); }}>Cancel</Button>
            <Button variant="destructive" disabled={!reasonText.trim()} onClick={handleTerminalMove}>
              Mark {reasonBecause ? ENDED_BECAUSE_WORDS[reasonBecause] : reasonStage}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
